import { useCallback, useRef, useState } from 'react';

import type {
  CreateAppointmentInput,
  CreateAppointmentVisitInput,
} from '@borradh-workspace/api-client/types';

import {
  type DoubleBookingConflict,
  useCreateAppointment,
} from '../api/create-appointment';
import { useCreateAppointmentVisit } from '../api/create-appointment-visit';
import type { Appointment, CreatedAppointmentVisit } from '../api/types';

import { useOptionalCalendar } from '@/components/calendar/contexts/calendar-context';
import {
  type AppointmentAdditionalService,
  type AppointmentCreateFormData,
  buildCreateAppointmentPayload,
  buildCreateAppointmentVisitPayload,
} from './appointment-create-form';
import { useAppointmentCreateContext } from './use-appointment-create-context';

interface UseCreateAppointmentFlowOptions {
  onSuccess?: (appointment: Appointment) => void;
  /**
   * A booking with more than one service. Defaults to calling `onSuccess`
   * with the visit's first appointment — the one any room pick or package was
   * applied to — so a surface that never adds services needs nothing new.
   */
  onVisitSuccess?: (visit: CreatedAppointmentVisit) => void;
  onError?: (error: Error) => void;
}

type PendingPayload =
  | { kind: 'single'; payload: CreateAppointmentInput }
  | { kind: 'visit'; payload: CreateAppointmentVisitInput };

/**
 * The single mutation entry point for creating an appointment. Takes validated
 * form values, builds the payload through the shared builder, and fires the
 * mutation. Every surface (desktop dialog, mobile route funnel, mobile drawer
 * funnel) goes through this — so there is exactly one place the API body is
 * constructed.
 *
 * ── The double-booking handshake (ENG-792) ─────────────────────────────────
 *
 * The server refuses a slot that overlaps an existing appointment for the same
 * team member, rather than creating it silently as it used to. That refusal
 * surfaces here as `conflict`: the surface renders it as a confirmation naming
 * the clashing appointment, and calls `confirmDoubleBooking()` if the user
 * accepts — which re-sends the SAME payload with `allowDoubleBooking: true`.
 *
 * The payload is stashed rather than rebuilt from the form so the second
 * attempt cannot differ from the one the conflict was reported for.
 */
export function useCreateAppointmentFlow(
  options?: UseCreateAppointmentFlowOptions
) {
  const context = useAppointmentCreateContext();
  const calendar = useOptionalCalendar();
  const locationId = calendar?.selectedLocationId ?? null;

  const [conflict, setConflict] = useState<DoubleBookingConflict | null>(null);
  const pendingPayload = useRef<PendingPayload | null>(null);

  const { createAppointment, isCreating } = useCreateAppointment({
    ...options,
    onSuccess: (appointment) => {
      setConflict(null);
      pendingPayload.current = null;
      options?.onSuccess?.(appointment);
    },
    onDoubleBookingConflict: setConflict,
  });

  // Several services: one appointment per service, linked as a visit.
  const { createAppointmentVisit, isCreatingVisit } = useCreateAppointmentVisit(
    {
      onError: options?.onError,
      onSuccess: (visit) => {
        setConflict(null);
        pendingPayload.current = null;
        if (options?.onVisitSuccess) options.onVisitSuccess(visit);
        else if (visit.appointments[0])
          options?.onSuccess?.(visit.appointments[0] as unknown as Appointment);
      },
      onDoubleBookingConflict: setConflict,
    }
  );

  /**
   * `extra` carries the few payload keys that are NOT form fields:
   * `resourceIds`, the front desk's explicit room/equipment pick, and
   * `allowResourceOverbook`, which says they were shown the clash inline and
   * chose to book anyway. The second is worthless without the first, and
   * omitting it when a chosen room IS busy would make the inline warning a
   * lie — the server would refuse the booking the operator was told would go
   * through. `clientPackageItemId` uses one of the customer's package services
   * on the visit, which the server then books at no charge.
   *
   * It is applied AFTER the shared builder rather than inside it on purpose:
   * the builder stays the one place the core body is constructed (title,
   * colour, start/end, ids), and a surface that passes nothing produces a
   * byte-identical payload to before — which is what keeps every org without
   * rooms, and the create-appointment form contract, untouched.
   */
  const submit = useCallback(
    (
      values: AppointmentCreateFormData,
      extra?: Pick<
        CreateAppointmentInput,
        | 'resourceIds'
        | 'allowResourceOverbook'
        | 'clientPackageItemId'
        | 'clientEmail'
      > & {
        /**
         * Services after the first, each with its own team member. Empty or
         * absent ⇒ the ordinary single booking, byte-identical to before.
         * Rooms and the package apply to the first service, as on screen.
         */
        additionalServices?: readonly AppointmentAdditionalService[];
      }
    ) => {
      if (extra?.additionalServices?.length) {
        const visit = buildCreateAppointmentVisitPayload(
          values,
          extra.additionalServices,
          context
        );
        const payload: CreateAppointmentVisitInput = {
          ...visit,
          ...(extra.resourceIds?.length
            ? {
                resourceIds: extra.resourceIds,
                ...(extra.allowResourceOverbook
                  ? { allowResourceOverbook: true }
                  : {}),
              }
            : {}),
          ...(extra.clientPackageItemId
            ? { clientPackageItemId: extra.clientPackageItemId }
            : {}),
          ...(extra.clientEmail ? { clientEmail: extra.clientEmail } : {}),
        };
        pendingPayload.current = { kind: 'visit', payload };
        createAppointmentVisit(payload);
        return;
      }

      const base = buildCreateAppointmentPayload(values, context);
      // Which site the booking is at. Read from the calendar when there is
      // one, so the allocator can drop rooms pinned to another branch — until
      // this was threaded through, NO caller supplied a location and every
      // allocation was unfiltered. Omitted when the surface has no calendar
      // (or the org has one site), which is the previous behaviour exactly.
      const withLocation = locationId === null ? base : { ...base, locationId };
      const withRooms = extra?.resourceIds?.length
        ? {
            ...withLocation,
            resourceIds: extra.resourceIds,
            ...(extra.allowResourceOverbook
              ? { allowResourceOverbook: true }
              : {}),
          }
        : withLocation;
      // The customer's package service, when staff left "Use package" on.
      const withPackage = extra?.clientPackageItemId
        ? { ...withRooms, clientPackageItemId: extra.clientPackageItemId }
        : withRooms;
      // Where an online booking's Meet link goes, for a client with no email.
      const payload = extra?.clientEmail
        ? { ...withPackage, clientEmail: extra.clientEmail }
        : withPackage;
      // Stash the payload the ROOMS keys are already on, so a "book it anyway"
      // retry re-sends the same rooms — rebuilding it from the form would drop
      // the operator's explicit pick on the second attempt.
      pendingPayload.current = { kind: 'single', payload };
      createAppointment(payload);
    },
    [createAppointment, createAppointmentVisit, context, locationId]
  );

  /** The user said "book it anyway" — re-send the same slot, with consent. */
  const confirmDoubleBooking = useCallback(() => {
    const pending = pendingPayload.current;
    if (!pending) return;
    setConflict(null);
    if (pending.kind === 'visit') {
      createAppointmentVisit({ ...pending.payload, allowDoubleBooking: true });
    } else {
      createAppointment({ ...pending.payload, allowDoubleBooking: true });
    }
  }, [createAppointment, createAppointmentVisit]);

  const dismissConflict = useCallback(() => {
    setConflict(null);
    pendingPayload.current = null;
  }, []);

  return {
    submit,
    isCreating: isCreating || isCreatingVisit,
    context,
    conflict,
    confirmDoubleBooking,
    dismissConflict,
  };
}
